// Hook to ensure MSAL is initialized before app startup
import { useState, useEffect } from 'react';
import { msalInstance } from '../contexts/AuthContext';
import { getCurrentConfig } from '../config';

export interface AppInitState {
  isInitialized: boolean;
  error: string | null;
}

/**
 * Hook to initialize MSAL before any components mount
 * This ensures token acquisition works immediately when services are called
 */
export const useAppInitialization = (): AppInitState => {
  const [initState, setInitState] = useState<AppInitState>({
    isInitialized: false,
    error: null
  });

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const config = getCurrentConfig();
        
        // Only initialize MSAL if authentication is enabled
        if (config.auth.enableAuthentication) {
          // Force MSAL initialization to complete before proceeding
          await msalInstance.initialize();
          
          // Verify MSAL is ready for token operations
          try {
            // Test that MSAL can perform basic operations
            msalInstance.getAllAccounts();
          } catch (testError) {
            throw new Error('MSAL initialization verification failed');
          }
        }
        
        setInitState({ isInitialized: true, error: null });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'App initialization failed';
        console.error('App initialization failed:', error);
        setInitState({ isInitialized: false, error: errorMessage });
      }
    };

    initializeApp();
  }, []);

  return initState;
};
